import styled from "@emotion/styled";
import { motion } from "@smartsheet/design-tokens";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { focusContentAfterStyles } from "../../../menu/styled/MenuItemBaseStyled";
import { menuTheme } from "../../../menu/styled/menuTheme";
import { selectV2Theme } from "../selectV2Theme";

export interface OptionBaseStyledProps {
    themeMode: RequiredThemeKeys;
    height?: number;
    isVirtualized?: boolean;
}

const shouldForwardProp = (propName: string): boolean => {
    switch (propName) {
        case "height":
        case "isVirtualized":
        case "themeMode":
            // Just add any extra props here :)
            return false;
    }
    return true;
};

export const OptionBaseStyled = styled("div", {
    shouldForwardProp,
})<OptionBaseStyledProps>(
    ({ themeMode, height, isVirtualized }) => `
    box-sizing: border-box;
    position: relative;
    border-radius: ${selectV2Theme.option.borderRadius(themeMode)};
    cursor: pointer;
    height: auto;
    line-height: 18px;
    margin: ${
        isVirtualized
            ? selectV2Theme.option.margin.virtualized(themeMode)
            : selectV2Theme.option.margin.default(themeMode)
    };
    min-height: ${height === undefined ? "none" : height + "px"};
    overflow: hidden;
    padding: ${selectV2Theme.padding(themeMode)};
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;

    background-color: ${menuTheme.backgroundColor.rest(themeMode)};
    color: ${menuTheme.fontColor.default.rest(themeMode)};

    &:not(.highlight-first-option-auto) {
        transition: background-color ${motion.animation.default};
        &:after {
            transition: ${motion.animation.default};
        }
    }

    > * {
        font-size: ${menuTheme.fontSize(themeMode)};
        white-space: normal;
        font-weight: 500;
    }

    // For situations like using a isReadOnly Ratings component as the option
    // we still want to show the pointer cursor, even though Ratings itself would show a default cursor
    * {
        cursor: pointer;
    }

    ${focusContentAfterStyles}

    &[data-highlighted="true"] {
        color: ${menuTheme.fontColor.default.focus(themeMode)};
        background-color: ${selectV2Theme.option.backgroundColor.hover(themeMode)};
        
        &:after{
            opacity: 1;
            transition: ${motion.animation.default};
        }
    }

    &:not([aria-disabled="true"]):active {
        background-color: ${selectV2Theme.option.backgroundColor.active(themeMode)};
    }

    &[aria-disabled="true"] {
        color: ${menuTheme.fontColor.default.disabled(themeMode)};
        background-color: transparent;
        > * {
            cursor: not-allowed;
        }
    }
`
);
